import { useMemo, useSyncExternalStore } from 'react';
import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { PreviewTemplate } from '../types';
import { getAlignedRecordings } from '@/player-config/src/utils/alignedRecordings';
import { AlignedRecordings, Clip } from '@/player-config/src/types/clip-types';
import { layoutConfigMap } from '@/player-config/src/types/speaker-types';
import { VIDEO_QUALITY, COMPOSITION_DIMENSIONS } from '@/player-config/src/constants/Remotion.constants';
import {
  getClipBroadcastId,
  isClipFromRecordingStudio,
  isLiveEventRemotionWithPreviewAndAlignedStream
} from '@/player-config/src/utils/clipUtils';
import { getClipThumbnailUrl } from '@/Pages/Sessions/AssetsTable/AssetTableClipThumbnail/ClipPreviewUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import {
  isLayoutActiveSpeaker,
  isLayoutAudiogram,
  isLayoutDefault,
  isLayoutFocus
} from '@/player-config/src/utils/layout';
import { getLayoutChangeAtCurrentFrame } from '@/App/remotion/MainPlayer/utils/playerConfig/compositeSegment/getLayoutChangesAndPlayRanges';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { selectAlignedRecordings, selectVideoFeeds } from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { getVideoFeedById } from '@/utils/videoFeeds';
import { isEntityScreenshare, isEntityUserFeed } from '@/player-config/src/utils/activityUtils';
import { VideoFeedType } from '@/player-config/src/types/video-feed-types';

export function useNewTemplatePreview({ template, clip }: { template: PreviewTemplate; clip: Clip }) {
  const clipBroadcastId = useMemo(() => getClipBroadcastId(clip) ?? '', [clip]);
  const alignedRecordingsStore = useContentMetadataStore(selectAlignedRecordings(clipBroadcastId));
  const videoFeeds = useContentMetadataStore(selectVideoFeeds(clipBroadcastId));
  const recordings = useMemo(
    () =>
      getAlignedRecordings({
        broadcastId: clipBroadcastId,
        snapshot: alignedRecordingsStore,
        contentMetadataStoreInstance: useContentMetadataStore
      }),
    [clipBroadcastId, alignedRecordingsStore]
  );
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const shouldUseNewTemplatePreview = useMemo(() => {
    // Audiogram template preview with remotion will require separate handling
    if (isLayoutAudiogram(template.layout) || template.intro) {
      return false;
    }
    return (
      isClipFromRecordingStudio(clip) ||
      template.layout === 'DEFAULT' ||
      isLiveEventRemotionWithPreviewAndAlignedStream(clip) ||
      isLayoutsWithVisionDataEnabled(clip)
    );
  }, [clip, template.layout, featureFlags]);

  const { compositionHeight, compositionWidth } = useMemo(() => {
    const videoSize = template.size;

    const videoQuality = VIDEO_QUALITY.FULL_HD;
    const compositionHeight = COMPOSITION_DIMENSIONS[videoSize][videoQuality].HEIGHT;
    const compositionWidth = COMPOSITION_DIMENSIONS[videoSize][videoQuality].WIDTH;

    return {
      compositionHeight,
      compositionWidth
    };
  }, [template.size]);

  const backgroundStyle = useMemo(() => {
    const backgroundImage = template.magicLayout?.backgroundImage;
    if (backgroundImage) {
      return { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }

    return { backgroundColor: template.magicLayout?.backgroundColor };
  }, [template.magicLayout]);

  const [videos, finalConfig] = useMemo(() => {
    const { size, layout, fit: fitMode, hide_borders: hideBorders } = template;

    /**
     * DEFAULT LAYOUT: Shows only the main clip thumbnail
     * - Used when no specific layout is selected or for simple previews
     * - Returns a single thumbnail (the main clip) with grid layout configuration
     * - Always uses GRID layout type with 1 video
     */
    if (isLayoutDefault(layout)) {
      return [
        [getClipThumbnailUrl(clip)],
        // changes from norm here are changing it to 'GRID' and videos length as 1
        layoutConfigMap[size]['GRID'][fitMode][getSpeakerLayoutBorderKey(!hideBorders)][1]
      ];
    }

    /**
     * AUDIOGRAM LAYOUT: No video thumbnails
     * - Returns empty arrays for both videos and configuration
     * - Template Preview is handled separately
     */
    if (isLayoutAudiogram(layout)) {
      return [[], []];
    }

    // Create a mutable copy of aligned recordings for processing
    let filteredRecordings: AlignedRecordings[] = [...recordings];

    // Get layout changes at the current frame to determine visible video order
    const layoutChange = getLayoutChangeAtCurrentFrame(clip);

    if (
      layoutChange?.videoIds.length === 1 &&
      getVideoFeedById(videoFeeds, layoutChange.videoIds[0])?.type === VideoFeedType.SHARED_VIDEO
    ) {
      const sharedVideoRecording = recordings.find(r => r.id === layoutChange.videoIds[0]);
      return [
        [sharedVideoRecording?.thumbnail ?? getClipThumbnailUrl(clip)],
        layoutConfigMap[size]['GRID'][fitMode][getSpeakerLayoutBorderKey(!hideBorders)][1]
      ];
    }

    /**
     * SORT RECORDINGS BY VISIBLE ORDER
     * - If layout changes exist and specify video order, sort recordings accordingly
     * - This ensures videos appear in the correct order as defined by the layout
     * - Videos not in visible_video_ids will be placed at the end (indexOf returns -1)
     */
    if (layoutChange && layoutChange.videoIds.length > 0) {
      const visible_video_ids = layoutChange.videoIds;
      filteredRecordings.sort((a, b) => visible_video_ids.indexOf(a.id) - visible_video_ids.indexOf(b.id));
    }

    /**
     * ACTIVE SPEAKER LAYOUT: Shows only the current active speaker
     * - Finds the first non-screenshare recording (assumed to be the active speaker)
     * - Returns only that speaker's thumbnail with single-video grid configuration
     * - If no non-screenshare recording exists, returns empty arrays
     */
    if (isLayoutActiveSpeaker(layout)) {
      /** Assumption is that active speaker will be sorted in visible_video_ids */
      const userRecordings = filteredRecordings.find(recording => isEntityUserFeed(recording.entity));

      if (!userRecordings) {
        return [[], []];
      }

      return [
        [userRecordings.thumbnail],
        layoutConfigMap[size]['GRID'][fitMode][getSpeakerLayoutBorderKey(!hideBorders)][1]
      ];
    }

    /**
     * FOCUS LAYOUT: Shows screenshare + up to 8 speakers
     * - Separates screenshare from speaker recordings
     * - Limits speakers to 8 maximum
     * - Adds screenshare at the beginning if it exists and layout allows it
     * - Uses FOCUS layout for multiple videos, GRID for single video
     */
    if (isLayoutFocus(layout)) {
      // filter out and keep single screenshare only
      const screenshareRecording = filteredRecordings.find(recording => isEntityScreenshare(recording.entity));

      filteredRecordings = filteredRecordings.filter(recording => isEntityUserFeed(recording.entity)).slice(0, 8);

      if ((layoutChange ? layoutChange.hasScreenshare : true) && screenshareRecording) {
        filteredRecordings.unshift(screenshareRecording);
      }

      const numberOfFilteredRecordings = filteredRecordings.length;

      return [
        filteredRecordings.map(r => r.thumbnail),
        layoutConfigMap[size][numberOfFilteredRecordings === 1 ? 'GRID' : 'FOCUS'][fitMode][
          getSpeakerLayoutBorderKey(!hideBorders)
        ][numberOfFilteredRecordings]
      ];
    }

    /**
     * DEFAULT GRID LAYOUT: Shows up to 8 speaker recordings
     * - Filters out screenshare recordings (speakers only)
     * - Limits to 8 videos maximum
     * - Uses GRID layout configuration
     * - This is the fallback for any other layout types
     */
    filteredRecordings = filteredRecordings
      .filter(recording => {
        return isEntityUserFeed(recording.entity);
      })
      .slice(0, 8);

    return [
      filteredRecordings.map(r => r.thumbnail),
      layoutConfigMap[size]['GRID'][fitMode][getSpeakerLayoutBorderKey(!hideBorders)][filteredRecordings.length]
    ];
  }, [recordings, template, clip.asset_metadata.scenes, videoFeeds]);

  return {
    shouldUseNewTemplatePreview,
    backgroundStyle,
    videos,
    finalConfig,
    compositionHeight,
    compositionWidth
  };
}
